/**
 * Americano Scheduler - Simple Round-Robin Algorithm
 *
 * SIMPLE APPROACH: N-1 rounds where each player plays exactly once per round
 * Formula: For N players → N-1 rounds
 *
 * Examples:
 * - 8 players → 7 rounds
 * - 12 players → 11 rounds
 * - 16 players → 15 rounds
 *
 * Each player plays N-1 matches total (once with each of the N-1 other players)
 * No rest periods - all players play every round
 * Guaranteed complete rotation
 */

const AmericanoSchedulerV2 = {
  warnings: [],

  generateSchedule(players, courtCount, roundCount, pointsPerMatch = 24, seed = null) {
    const N = players.length;
    this.warnings = [];

    const generationSeed = seed !== null ? seed : Date.now();
    console.log(`🎾 Génération planning Americano: ${N} joueurs, ${roundCount} rounds (seed: ${generationSeed})`);

    const shuffledPlayers = this.seededShuffle([...players], generationSeed);

    if (N % 4 !== 0) {
      this.warnings.push(`⚠️ Nombre de joueurs doit être multiple de 4 pour format 2v2`);
    }

    const correctRoundCount = N - 1;

    if (roundCount < correctRoundCount) {
      this.warnings.push(
        `⚠️ Pour rotation complète avec ${N} joueurs, il faut ${correctRoundCount} rounds ` +
        `(actuellement: ${roundCount}). Rotation sera partielle.`
      );
    } else if (roundCount > correctRoundCount) {
      console.log(`ℹ️ ${roundCount} rounds demandés, mais ${correctRoundCount} suffisent pour rotation complète`);
    }

    const rounds = [];
    const startTime = Date.now();

    const actualRounds = Math.min(roundCount, correctRoundCount);
    const rotation = this.generateCircularRoundRobin(shuffledPlayers, actualRounds);

    rotation.forEach((round, index) => {
      rounds.push({
        roundIndex: index,
        matches: round.matches,
        restingPlayers: round.restingPlayers || []
      });
    });

    const elapsed = Date.now() - startTime;
    console.log(`✅ Planning généré en ${elapsed}ms`);

    const matrices = this.calculateMatricesFromSchedule(rounds, N);

    if (actualRounds === correctRoundCount) {
      console.log(`🎉 Rotation complète! Chaque joueur joue avec chacun des ${N - 1} autres.`);
    } else {
      console.log(`⚠️ Rotation partielle: ${actualRounds}/${correctRoundCount} rounds`);
    }

    return {
      rounds,
      warnings: this.warnings,
      partnerMatrix: matrices.partnerMatrix,
      opponentMatrix: matrices.opponentMatrix,
      sitOutCount: matrices.sitOutCount,
      rotationStats: matrices.rotationStats,
      seed: generationSeed
    };
  },

  generateCircularRoundRobin(players, roundCount) {
    const N = players.length;
    const playerIds = players.map(p => p.id);
    const rounds = [];

    const partnerMatrix = Array(N).fill(0).map(() => Array(N).fill(0));
    const opponentMatrix = Array(N).fill(0).map(() => Array(N).fill(0));
    const sitOutCount = Array(N).fill(0);

    for (let r = 0; r < roundCount; r++) {
      const roundData = this.generateRoundWithUniquePartners(
        playerIds,
        partnerMatrix,
        opponentMatrix,
        sitOutCount
      );

      if (!roundData) {
        console.error(`Failed to generate round ${r + 1} with unique partners`);
        break;
      }

      rounds.push({
        matches: roundData.matches,
        restingPlayers: roundData.restingPlayers
      });

      roundData.restingPlayers.forEach(playerId => {
        sitOutCount[playerId]++;
      });

      roundData.matches.forEach(match => {
        const [p1, p2] = match.team1;
        const [p3, p4] = match.team2;

        partnerMatrix[p1][p2]++;
        partnerMatrix[p2][p1]++;
        partnerMatrix[p3][p4]++;
        partnerMatrix[p4][p3]++;

        opponentMatrix[p1][p3]++;
        opponentMatrix[p1][p4]++;
        opponentMatrix[p2][p3]++;
        opponentMatrix[p2][p4]++;
        opponentMatrix[p3][p1]++;
        opponentMatrix[p3][p2]++;
        opponentMatrix[p4][p1]++;
        opponentMatrix[p4][p2]++;
      });
    }

    return rounds;
  },

  generateRoundWithUniquePartners(playerIds, partnerMatrix, opponentMatrix, sitOutCount) {
    const N = playerIds.length;
    const matchCount = Math.floor(N / 4);
    const playersNeeded = matchCount * 4;

    const sortedPlayers = [...playerIds].sort((a, b) => sitOutCount[a] - sitOutCount[b]);

    const playingPlayers = sortedPlayers.slice(0, playersNeeded);
    const restingPlayers = sortedPlayers.slice(playersNeeded);

    const availablePlayers = [...playingPlayers];
    const matches = [];

    for (let m = 0; m < matchCount; m++) {
      const match = this.findBestMatch(availablePlayers, partnerMatrix, opponentMatrix);

      if (!match) return null;

      matches.push({
        courtIndex: m,
        team1: match.team1,
        team2: match.team2,
        detailedScore: null,
        validated: false
      });

      match.team1.forEach(p => {
        const idx = availablePlayers.indexOf(p);
        if (idx > -1) availablePlayers.splice(idx, 1);
      });
      match.team2.forEach(p => {
        const idx = availablePlayers.indexOf(p);
        if (idx > -1) availablePlayers.splice(idx, 1);
      });
    }

    return { matches, restingPlayers };
  },

  findBestMatch(available, partnerMatrix, opponentMatrix) {
    if (available.length < 4) return null;

    let bestMatch = null;
    let bestScore = Infinity;

    for (let i = 0; i < available.length - 3; i++) {
      for (let j = i + 1; j < available.length - 2; j++) {
        for (let k = j + 1; k < available.length - 1; k++) {
          for (let l = k + 1; l < available.length; l++) {
            const fourPlayers = [available[i], available[j], available[k], available[l]];

            const configs = [
              { team1: [fourPlayers[0], fourPlayers[1]], team2: [fourPlayers[2], fourPlayers[3]] },
              { team1: [fourPlayers[0], fourPlayers[2]], team2: [fourPlayers[1], fourPlayers[3]] },
              { team1: [fourPlayers[0], fourPlayers[3]], team2: [fourPlayers[1], fourPlayers[2]] }
            ];

            for (const config of configs) {
              const score = this.evaluateMatchConfig(config, partnerMatrix, opponentMatrix);
              if (score < bestScore) {
                bestScore = score;
                bestMatch = config;
              }
            }
          }
        }
      }
    }

    return bestMatch;
  },

  evaluateMatchConfig(config, partnerMatrix, opponentMatrix) {
    const [p1, p2] = config.team1;
    const [p3, p4] = config.team2;

    const partnerScore = (partnerMatrix[p1][p2] + partnerMatrix[p3][p4]) * 1000;

    const opponentScore =
      opponentMatrix[p1][p3] + opponentMatrix[p1][p4] +
      opponentMatrix[p2][p3] + opponentMatrix[p2][p4];

    const opponentCounts = [
      opponentMatrix[p1][p3], opponentMatrix[p1][p4],
      opponentMatrix[p2][p3], opponentMatrix[p2][p4]
    ];
    const avgOpponent = opponentScore / 4;
    const variance = opponentCounts.reduce((sum, count) =>
      sum + Math.pow(count - avgOpponent, 2), 0);

    return partnerScore + opponentScore + variance;
  },

  calculateMatricesFromSchedule(rounds, N) {
    const partnerMatrix = Array(N).fill(0).map(() => Array(N).fill(0));
    const opponentMatrix = Array(N).fill(0).map(() => Array(N).fill(0));
    const sitOutCount = Array(N).fill(0);

    rounds.forEach(round => {
      round.matches.forEach(match => {
        const { team1, team2 } = match;

        partnerMatrix[team1[0]][team1[1]]++;
        partnerMatrix[team1[1]][team1[0]]++;
        partnerMatrix[team2[0]][team2[1]]++;
        partnerMatrix[team2[1]][team2[0]]++;

        const allPlayers = [...team1, ...team2];
        for (let i = 0; i < 2; i++) {
          for (let j = 2; j < 4; j++) {
            opponentMatrix[allPlayers[i]][allPlayers[j]]++;
            opponentMatrix[allPlayers[j]][allPlayers[i]]++;
          }
        }
      });

      if (round.restingPlayers) {
        round.restingPlayers.forEach(playerId => {
          sitOutCount[playerId]++;
        });
      }
    });

    let completedPartnerships = 0;
    const totalPossible = (N * (N - 1)) / 2;

    for (let i = 0; i < N; i++) {
      for (let j = i + 1; j < N; j++) {
        if (partnerMatrix[i][j] >= 1) {
          completedPartnerships++;
        }
      }
    }

    const rotationStats = {
      completedPartnerships,
      totalPossible,
      percentage: (completedPartnerships / totalPossible) * 100,
      isComplete: completedPartnerships === totalPossible
    };

    return { partnerMatrix, opponentMatrix, sitOutCount, rotationStats };
  },

  seededRandom(seed) {
    let state = seed;
    return function () {
      state = (state * 1664525 + 1013904223) % 4294967296;
      return state / 4294967296;
    };
  },

  seededShuffle(array, seed) {
    const shuffled = [...array];
    const random = this.seededRandom(seed);

    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  },

  reset() {
    this.warnings = [];
  },

  async generateBestOfN(players, courtCount, roundCount, pointsPerMatch = 24, n = 1, progressCallback = null) {
    console.log(`🎲 Génération de ${n} planning(s) Americano avec randomisation...`);

    this.reset();

    const candidates = [];

    for (let i = 0; i < n; i++) {
      const seed = Date.now() + i * 1000;
      const schedule = this.generateSchedule(players, courtCount, roundCount, pointsPerMatch, seed);
      candidates.push(schedule);

      if (progressCallback) {
        progressCallback(i + 1, n, ((i + 1) / n) * 100);
      }
    }

    const bestSchedule = candidates[0];

    console.log(`✅ ${n} planning(s) généré(s) avec différents ordres (seed utilisé: ${bestSchedule.seed})`);

    return {
      schedule: bestSchedule,
      candidatesGenerated: n,
      bestRotationPercentage: 100,
      avgPercentage: 100,
      minPercentage: 100,
      allPercentages: candidates.map(() => 100)
    };
  }
};

export default AmericanoSchedulerV2;
